import { getPropertyValue, getSocketURL, mapErrorMessage } from '@deriv/shared';
import { localize } from '@deriv-com/translations';

import { clearTokens, generateOAuthURL } from './oauth';
import WS from './ws-methods';

import ServerTime from '_common/base/server_time';
import BinarySocket from '_common/base/socket_base';

let client_store, common_store;

// TODO: update commented statements to the corresponding functions from app
const BinarySocketGeneral = (() => {
    // Removed session management variables - not needed for trading app

    let responseTimeoutErrorTimer = null;

    const onDisconnect = () => {
        clearTimeout(responseTimeoutErrorTimer);
        common_store.setIsSocketOpened(false);
    };

    // Transient WS errors during a reconnect burst should NOT log the user out or
    // show a terminal "refresh this page" screen — network_monitor_base already
    // reopens the socket on close with exponential-ish backoff, and onOpen below
    // clears any stale error state. derivdtrader has no equivalent escalation.
    const onConnectionError = () => {
        // eslint-disable-next-line no-console
        console.warn('WebSocket connection error — relying on reconnect loop');
    };

    const onOpen = is_ready => {
        responseTimeoutErrorTimer = setTimeout(() => {
            const expectedResponseTypes = WS?.get?.()?.expect_response_types || {};
            const pendingResponseTypes = Object.keys(expectedResponseTypes).filter(
                key => expectedResponseTypes[key].state === 'pending'
            );

            const error = new Error('deriv-api: no message received after 30s');
            error.userId = client_store?.loginid;

            // eslint-disable-next-line no-console
            console.error(error.message, { websocketUrl: getSocketURL(), pendingResponseTypes });
        }, 30000);

        if (is_ready) {
            ServerTime.init(() => common_store.setServerTime(ServerTime.get()));
            common_store.setIsSocketOpened(true);
            // Clear any stale terminal-error state from a previous disconnect burst.
            // Without this, the "Connection failed" fullscreen error persists even
            // after the socket successfully reconnects.
            if (common_store.has_error) {
                common_store.setError(false, null);
            }
        }
    };

    const onMessage = response => {
        clearTimeout(responseTimeoutErrorTimer);
        handleError(response);

        // Error responses don't carry the payload the case branches assume
        // (e.g. an InvalidToken authorize-error has `{error:...}` and no
        // `authorize` field — `authorizeAccount` would then destructure
        // `undefined.loginid` and crash). handleError has already done the
        // right thing for known error codes; stop here.
        if (response.error) {
            return;
        }

        switch (response.msg_type) {
            case 'balance':
                if (response.balance) {
                    if (!client_store.is_authorize) {
                        // First balance after (re)connect in v4 mode — use as auth confirmation.
                        // Some OTP WS implementations omit loginid from the balance response;
                        // fall back to active_loginid so auth always completes.
                        authorizeAccount(response);
                        // Notify parent iframe host that trading components are ready
                        if (window !== window.top) {
                            window.parent.postMessage({ type: 'COMPONENTS_READY' }, '*');
                        }
                    } else {
                        // Subsequent balance updates — just update the balance
                        ResponseHandlers.balanceActiveAccount(response);
                    }
                }
                break;
            // no default
        }
    };

    const setBalanceActiveAccount = obj_balance => {
        client_store.setBalanceActiveAccount(obj_balance);
    };

    const handleError = response => {
        const msg_type = response.msg_type;
        const error_code = getPropertyValue(response, ['error', 'code']);
        switch (error_code) {
            case 'WrongResponse':
                if (msg_type === 'balance') {
                    WS.forgetAll('balance').then(subscribeBalance);
                }
                break;
            case 'RateLimit':
                common_store.setError(true, {
                    message: localize('You have reached the rate limit of requests per second. Please try later.'),
                });
                break;
            case 'InvalidAppID':
                common_store.setError(true, { message: mapErrorMessage(response.error) });
                break;
            case 'DisabledClient':
                common_store.setError(true, { message: mapErrorMessage(response.error) });
                break;
            case 'AuthorizationRequired': {
                if (msg_type === 'buy') {
                    return;
                }
                if (window !== window.top) {
                    // Embedded: notify parent and let it recover. Hard-logging out
                    // here would discard cached state the parent is about to refresh.
                    window.parent.postMessage({ type: 'DTRADER_TOKEN_INVALID' }, '*');
                } else {
                    client_store.logout();
                }
                break;
            }
            case 'InvalidToken': {
                const is_embedded = window !== window.top;
                if (is_embedded) {
                    // Embedded iframe: do NOT clear our own auth state here. The
                    // parent (newapiOnly) owns the token lifecycle — it will try
                    // a refresh and re-send ACCOUNT_CHANGED with a fresh token. If
                    // we wipe state now we have to cold-boot everything from
                    // scratch when parent recovers, which produces the "switching
                    // account → live → logout" flicker users were seeing. Just
                    // notify and wait.
                    window.parent.postMessage({ type: 'DTRADER_TOKEN_INVALID' }, '*');
                } else {
                    // Standalone new API: redirect to OAuth login
                    clearTokens();
                    generateOAuthURL().then(url => window.location.replace(url));
                }
                break;
            }
            default:
                break;
        }
    };

    const init = store => {
        client_store = store.client;
        common_store = store.common;

        return {
            onDisconnect,
            onOpen,
            onMessage,
            onConnectionError,
        };
    };

    const subscribeBalance = () => {
        WS.subscribeBalance(ResponseHandlers.balanceActiveAccount);
    };

    const authorizeAccount = response => {
        // Balance response now contains authorization data
        // Transform if needed to match authorize format
        let authorize_data = response;

        // If response is balance format, transform to authorize format
        if (response.balance && !response.authorize) {
            // The v4 balance response only contains financial fields.
            // Fall back to active_loginid so auth completes even when the OTP WS
            // balance response omits loginid or user_id.
            const fallback_loginid =
                response.balance.loginid ||
                sessionStorage.getItem('active_loginid') ||
                localStorage.getItem('active_loginid') ||
                '';
            const fallback_user_id =
                response.balance.user_id ||
                fallback_loginid;
            authorize_data = {
                authorize: {
                    loginid: fallback_loginid,
                    balance: response.balance.balance,
                    currency: response.balance.currency,
                    email: response.balance.email || '',
                    landing_company_name: response.balance.landing_company_name || '',
                    country: response.balance.country || '',
                    user_id: fallback_user_id,
                    preferred_language: response.balance.preferred_language || '',
                },
            };
        }

        client_store.responseAuthorize(authorize_data);
        client_store.setIsAuthorize(true); // Set BEFORE anything that depends on it
        client_store.setIsLoggingIn(false); // Auth complete — clear the logging-in flag
        BinarySocket.sendBuffered(); // Now buffered calls see is_authorize = true
    };

    return {
        init,
        setBalanceActiveAccount,
        authorizeAccount,
    };
})();

export default BinarySocketGeneral;

const ResponseHandlers = (() => {
    const balanceActiveAccount = response => {
        if (!response.error) {
            const balance = response.balance?.balance || response.balance;
            if (balance !== undefined && balance !== null && balance !== '') {
                // Use the loginid the WS RESPONSE belongs to, not the currently
                // active one. setBalanceActiveAccount gates on
                // (obj_balance.loginid === current_account.loginid) — but if we
                // pre-fill it with current_account.loginid the gate always
                // passes, even when the response is actually a stale per-account
                // update for a different account. That stomps the displayed
                // balance with the wrong account's value during account switches.
                const response_loginid =
                    response.balance?.loginid ||
                    response.echo_req?.loginid ||
                    client_store?.loginid;
                BinarySocketGeneral.setBalanceActiveAccount({
                    balance,
                    loginid: response_loginid,
                });
            }
        }
    };

    return {
        balanceActiveAccount,
    };
})();
